import { useEffect, useRef, useState } from "react";
import { CalendarCheck, Camera, GitFork, Lightbulb, MapPin, Monitor, Truck, Users } from "lucide-react";
// Placeholder photos (optimized copies of existing event shots) until the final images are ready.
import conceptImg from "../../assets/images/whatwedo/eventsExperiences/eventsServices/01.jpg";
import venueImg from "../../assets/images/whatwedo/eventsExperiences/eventsServices/02.jpg";
import productionImg from "../../assets/images/whatwedo/eventsExperiences/eventsServices/03.jpg";
import vendorsImg from "../../assets/images/whatwedo/eventsExperiences/eventsServices/04.jpg";
import talentImg from "../../assets/images/eventsExperiences/services/talent.jpg";
import logisticsImg from "../../assets/images/whatwedo/eventsExperiences/eventsServices/06.jpg";
import onsiteImg from "../../assets/images/eventsExperiences/services/onsite.jpg";
import contentImg from "../../assets/images/eventsExperiences/services/content.jpg";
import useStepGate from "./useStepGate";

const SERVICES = [
  { icon: Lightbulb, image: conceptImg, title: "Concept & Budget", desc: "The idea, theme, timeline and a costed plan, agreed before anything is booked." },
  { icon: MapPin, image: venueImg, title: "Venue & Permissions", desc: "Venue shortlist and site visits, plus the permits and approvals the event needs." },
  { icon: Monitor, image: productionImg, title: "Production & Décor", desc: "Stage, sound, lighting, LED screens, branding and décor, designed, built and tested." },
  { icon: GitFork, image: vendorsImg, title: "Vendor Management", desc: "Sourcing, negotiating and managing caterers, AV, security and every other vendor on site." },
  { icon: Users, image: talentImg, title: "Talent & Guests", desc: "Artists, hosts, celebrities and creators booked and managed, with guest lists and invites handled." },
  { icon: Truck, image: logisticsImg, title: "Logistics & Load-in", desc: "Equipment, transport, travel and stay, rehearsals, and load-in and load-out on schedule." },
  { icon: CalendarCheck, image: onsiteImg, title: "On-site Management", desc: "A team on the ground running the show to the minute and solving problems as they happen." },
  { icon: Camera, image: contentImg, title: "Content & Wrap-up", desc: "Photos, a highlight reel and a recap video, followed by a closing report with costs reconciled." },
];

const n = SERVICES.length;
const SCALES = [1.2, 1, 0.9, 0.8, 0.8]; // by distance from the active circle

// Shortest signed distance from the active item, wrapping around the ends.
const offsetOf = (i, active) => ((i - active + n + n / 2) % n) - n / 2;
const pad = (i) => String(i + 1).padStart(2, "0");

const EventsServices = () => {
  const [active, setActive] = useState(0);
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const section = useRef(null);
  const { icon: Icon, title, desc } = SERVICES[active];
  useStepGate(section, n, active, () => setActive((i) => (i + 1) % n)); // scrolling also steps until all are seen

  useEffect(() => {
    const node = section.current;
    if (!node || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: 0.25 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible || paused) return;
    const timer = window.setInterval(() => setActive((i) => (i + 1) % n), 4500);
    return () => window.clearInterval(timer);
  }, [visible, paused]);

  return (
    <section ref={section} className="ee-svc" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocusCapture={() => setPaused(true)} onBlurCapture={() => setPaused(false)}>
      {/* Full-screen photo of the active service, cross-fading between items. */}
      {SERVICES.map((s, i) => (
        <div
          key={s.title}
          className={`ee-svc-bg ${i === active ? "is-active" : ""}`}
          style={{ backgroundImage: `url(${s.image})` }}
        />
      ))}
      <div className="ee-svc-shade" />

      <div className="container ee-svc-inner">
        <div className="ee-svc-content">
          <span className="ee-svc-pill" data-aos="fade-up" data-aos-once="true">
            <b>{pad(active)}</b> We handle everything
          </span>
          <h2 className="ee-title" data-aos="fade-up" data-aos-once="true">
            The details behind <span>a smooth event.</span>
          </h2>
          <p className="ee-intro" data-aos="fade-up" data-aos-once="true">
            From the first budget to the last load-out, one team owns it. You deal with us, not with twenty vendors.
          </p>

          {/* Re-keyed on change so the active service fades up. */}
          <div key={active} className="ee-svc-active">
            <span className="ee-icon">
              <Icon size={18} />
            </span>
            <h3>{title}</h3>
            <p>{desc}</p>
          </div>
        </div>

        <div className="ee-svc-orbit">
          {SERVICES.map((s, i) => {
            const offset = offsetOf(i, active);
            const abs = Math.abs(offset);
            return (
              <button
                type="button"
                key={s.title}
                className={`ee-svc-item ${offset === 0 ? "is-active" : ""}`}
                onClick={() => setActive(i)}
                aria-label={s.title}
                style={{ "--o": offset, "--a": abs, "--s": SCALES[abs], opacity: abs > 2 ? 0 : 1 }}
              >
                <img loading="lazy" decoding="async" src={s.image} alt="" />
                <span className="ee-svc-label">
                  <strong>{s.title}</strong>
                  <small>{pad(i)}</small>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="ee-svc-dots">
        {SERVICES.map((s, i) => (
          <button
            type="button"
            key={s.title}
            className={i === active ? "is-active" : ""}
            onClick={() => setActive(i)}
            aria-label={`Show ${s.title}`}
          />
        ))}
      </div>
    </section>
  );
};

export default EventsServices;
